import { slugify } from "./slug";

function pad(n) {
	return n < 10 ? "0" + n : "" + n;
}

function startOfDay(d) {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Parse a local YYYY-MM-DD key back into a local Date (midnight).
function fromDayKey(key) {
	if (!key || typeof key !== "string") return null;
	const parts = key.split("-");
	if (parts.length !== 3) return null;
	return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function avg(sum, n) {
	return n > 0 ? Math.round(sum / n) : 0;
}

// Local-time YYYY-MM-DD key (consistent for heatmap + streaks).
export function toDayKey(dateLike) {
	if (!dateLike) return null;
	const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
	if (isNaN(d.getTime())) return null;
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const DIFFICULTIES = ["Easy", "Medium", "Hard"];

// Aggregate everything the dashboard needs in one pass.
export function computeStats(topics) {
	topics = topics || [];
	let totalQuestions = 0;
	let totalSolved = 0;
	let bookmarked = 0;
	let revise = 0;
	let withNotes = 0;
	let custom = 0;
	let customTopics = 0;
	const byDifficulty = { Easy: 0, Medium: 0, Hard: 0, Unset: 0 };
	const byTopic = [];
	const solvedDates = [];

	// ---- solve-time analytics accumulators ----
	let timedCount = 0;
	let timeSum = 0;
	const timeByDiff = { Easy: { sum: 0, n: 0 }, Medium: { sum: 0, n: 0 }, Hard: { sum: 0, n: 0 } };
	const timeByTopic = {};

	topics.forEach((topic) => {
		const questions = topic.questions || [];
		let solved = 0;
		if (topic.Custom) customTopics += 1;
		questions.forEach((q) => {
			totalQuestions += 1;
			if (q.Done) {
				totalSolved += 1;
				solved += 1;
				const key = toDayKey(q.SolvedAt);
				if (key) solvedDates.push(key);
				if (q.TimeSpent > 0) {
					timedCount += 1;
					timeSum += q.TimeSpent;
					if (timeByDiff[q.Difficulty]) {
						timeByDiff[q.Difficulty].sum += q.TimeSpent;
						timeByDiff[q.Difficulty].n += 1;
					}
					const tn = topic.topicName;
					if (!timeByTopic[tn]) timeByTopic[tn] = { sum: 0, n: 0 };
					timeByTopic[tn].sum += q.TimeSpent;
					timeByTopic[tn].n += 1;
				}
			}
			if (q.Bookmark) bookmarked += 1;
			if (q.Revise) revise += 1;
			if (q.Notes && q.Notes.length > 0) withNotes += 1;
			if (q.Custom) custom += 1;
			if (DIFFICULTIES.indexOf(q.Difficulty) !== -1) byDifficulty[q.Difficulty] += 1;
			else byDifficulty.Unset += 1;
		});
		byTopic.push({
			topicName: topic.topicName,
			slug: slugify(topic.topicName),
			total: questions.length,
			solved,
			percent: questions.length ? Math.round((solved / questions.length) * 100) : 0,
			started: !!topic.started,
			custom: !!topic.Custom,
		});
	});

	return {
		totalQuestions,
		totalSolved,
		percent: totalQuestions ? Math.round((totalSolved / totalQuestions) * 100) : 0,
		bookmarked,
		revise,
		withNotes,
		custom,
		customTopics,
		byDifficulty,
		byTopic,
		solvedDates,
		timeStats: {
			timedCount,
			avgOverall: avg(timeSum, timedCount),
			avgByDifficulty: {
				Easy: avg(timeByDiff.Easy.sum, timeByDiff.Easy.n),
				Medium: avg(timeByDiff.Medium.sum, timeByDiff.Medium.n),
				Hard: avg(timeByDiff.Hard.sum, timeByDiff.Hard.n),
			},
			slowestTopics: Object.keys(timeByTopic)
				.map((name) => ({ topicName: name, avg: avg(timeByTopic[name].sum, timeByTopic[name].n), count: timeByTopic[name].n }))
				.sort((a, b) => b.avg - a.avg)
				.slice(0, 3),
		},
		recent: recentCounts(solvedDates),
	};
}

// Count problems solved in the last 7 and 30 days from a list of local day-keys.
function recentCounts(dayKeys) {
	const now = startOfDay(new Date());
	let last7 = 0;
	let last30 = 0;
	(dayKeys || []).forEach((k) => {
		const d = fromDayKey(k);
		if (!d) return;
		const diff = Math.round((now - d) / 86400000);
		if (diff < 0) return;
		if (diff < 7) last7 += 1;
		if (diff < 30) last30 += 1;
	});
	return { last7, last30 };
}

// GitHub-style contribution grid for the last `weeks` weeks.
export function buildHeatmap(dayKeys, weeks) {
	weeks = weeks || 20;
	const counts = {};
	(dayKeys || []).forEach((k) => {
		if (k) counts[k] = (counts[k] || 0) + 1;
	});

	const today = startOfDay(new Date());
	const endRef = new Date(today);
	endRef.setDate(endRef.getDate() + (6 - today.getDay())); // Saturday of current week
	const start = new Date(endRef);
	start.setDate(start.getDate() - (weeks * 7 - 1));

	const grid = [];
	const cursor = new Date(start);
	for (let w = 0; w < weeks; w++) {
		const col = [];
		for (let d = 0; d < 7; d++) {
			const key = toDayKey(cursor);
			col.push({ date: key, count: counts[key] || 0, future: cursor > today });
			cursor.setDate(cursor.getDate() + 1);
		}
		grid.push(col);
	}

	let max = 0;
	Object.keys(counts).forEach((k) => {
		if (counts[k] > max) max = counts[k];
	});
	return { grid, max };
}

// Current streak (counts today, or yesterday if nothing solved yet today).
export function computeStreak(dayKeys) {
	const set = new Set((dayKeys || []).filter(Boolean));
	if (set.size === 0) return 0;
	const cursor = startOfDay(new Date());
	if (!set.has(toDayKey(cursor))) {
		cursor.setDate(cursor.getDate() - 1);
		if (!set.has(toDayKey(cursor))) return 0;
	}
	let streak = 0;
	while (set.has(toDayKey(cursor))) {
		streak += 1;
		cursor.setDate(cursor.getDate() - 1);
	}
	return streak;
}

// Longest streak ever recorded in the solved-date set.
export function longestStreak(dayKeys) {
	const days = Array.from(new Set((dayKeys || []).filter(Boolean))).sort();
	if (days.length === 0) return 0;
	let best = 1;
	let run = 1;
	for (let i = 1; i < days.length; i++) {
		const prev = new Date(days[i - 1]);
		const cur = new Date(days[i]);
		const diff = Math.round((cur - prev) / 86400000);
		if (diff === 1) run += 1;
		else run = 1;
		if (run > best) best = run;
	}
	return best;
}
